/**
 * Launcher V2 PR C U6 — dirty-copy/token flags + live sample-contact preview
 * (R3).
 *
 * REUSE, DON'T RE-DERIVE (team convention): the engine is v1's
 * `interpolateTemplate` / `checkCopyVarGaps` / `COPY_VARIABLE_FIELDS`
 * (`mini-apps/launcher/components/waterfallTypes.ts`). This module is a
 * thin, pure, DOM-free adapter that maps a v2 `SequenceStep`'s copy + one
 * sample member row (the exact `CampaignMember` shape `useLauncherMembers`
 * already returns) into those utilities' input shape — no new gap
 * vocabulary, no new interpolation rules. Whatever `checkCopyVarGaps`
 * reports is exactly what this module reports; whatever `interpolateTemplate`
 * produces is exactly what the preview shows.
 *
 * ── WHAT U1 CHANGED UNDERNEATH THIS ADAPTER ───────────────────────────────
 * U1's changes flowed through without an edit here. U2's did NOT, and that
 * claim was the bug: `previewStepCopy` called `interpolateTemplate` once per
 * field, which forwarded no resolution mode, while the send path resolved
 * subjects in single-line mode. A `drop-line` token in a subject therefore
 * previewed as an emptied subject and sent as a shortened one (Codex A4) —
 * "same function underneath" is not parity when the function's mode is a
 * per-call-site option. It now calls `resolveStepCopy`, the one step-shaped
 * entry point the send path uses, and names no mode at all. The two previews
 * still have split jobs worth naming:
 *
 * - `previewStepCopy` (via `resolveStepCopy`) is FINAL COPY. It is the
 *   same resolver the send path uses, so each empty token takes its declared
 *   `emptyFallback` — substituted (`{{First Name}}` → `there`) or the whole
 *   enclosing line removed. It no longer renders a known-but-empty token as a
 *   hole in the sentence, which is the bug the send PRD exists to kill
 *   (`'Hey , this is  from Clay'`, shipped from real prod copy).
 * - `previewStepCopySegments` (via `interpolateTemplateSegments`) is the
 *   DIAGNOSTIC view. It still reports an empty token as an `empty` chip
 *   carrying the raw `{{Token}}` text, because its job is to show the rep
 *   WHICH tokens did not resolve, not what the recipient reads.
 *
 * An UNKNOWN token (a typo, a Gong-native spelling) is no longer counted as a
 * gap by `stepTokenGaps`/`rosterTokenGaps`: no member can ever "have" it, so it
 * is a copy defect for the authoring flags to raise, not per-member missing
 * data. A CLAY-OWNED token (`{{Company Name}}`, `{{Sender First Name}}`,
 * `{{Sender Meeting Link}}`) is excluded from the gap check for a different
 * reason — Terra is not supposed to resolve it, Clay fills it at send — and
 * `previewStepCopySegments` renders it as a `deferred` chip carrying Terra's
 * best guess plus a "set by Clay" caption (2026-09-16).
 *
 * DOM-free for the same reason `sequence-readiness.ts` / `review-gate.ts`
 * are: `bun:test` has no DOM under `mini-apps/`, so any logic living inside a
 * Glaze-importing `.tsx` file is untestable there.
 */

import type { CampaignMember } from '../_shared/launcher-campaign.types';
import {
  normalizeGongTokensInStep,
  normalizeGongTokensInSteps,
} from './copy-generation-rules';
import {
  buildTemplateVars,
  checkCopyVarGaps,
  checkCopyVarGapsByMemberId,
  interpolateTemplateSegments,
  resolveStepCopy,
  SENDER_TOKENS,
  type SenderValue,
  type TemplateSegment,
} from '../_shared/launcher-copy-tokens';
import type { SequenceStep } from './schemas';

const SENDER_TOKEN_VALUES: ReadonlySet<string> = new Set(SENDER_TOKENS.map((t) => t.value));

/**
 * v2's `SequenceMapping.sender` is a free-form string (a sender token's raw
 * value, or a static email address — see `SequenceMappingSchema`'s own doc
 * comment for the reserved vocabulary). v1's `interpolateTemplate`/
 * `checkCopyVarGaps` want the tagged `SenderValue` shape instead. This is the
 * one place that conversion happens, and it mirrors `sender-choice.ts`'s own
 * `isSenderToken` check exactly so the two never drift out of sync.
 */
export function toSenderValue(sender: string | null | undefined): SenderValue | null {
  if (!sender) return null;
  return SENDER_TOKEN_VALUES.has(sender)
    ? { type: 'token', token: sender as SenderValue['token'] }
    : { type: 'static', email: sender };
}

/**
 * The campaign fields `interpolateTemplate`/`checkCopyVarGaps` read — a
 * narrow, adapter-owned shape so a caller only has to hand over what it
 * actually has, not a whole `Campaign` object.
 */
export interface CopyPreviewCampaign {
  name?: string;
  event_date?: string | null;
  start_date?: string | null;
  type?: string;
}

/** The step copy fields this module reads — `SequenceStep`'s copy, or a live unsaved draft of the same shape. */
export type CopyPreviewStep = Pick<SequenceStep, 'subject' | 'body'>;

export interface CopyPreviewResult {
  subject: string;
  body: string;
}

export interface CopyTokenGap {
  variable: string;
  count: number;
  memberNames: string[];
}

/** One token's roster-wide gap aggregate — U10's `{token, count, memberIds}` shape (plan's roster-gap aggregation spec). */
export interface RosterTokenGap {
  token: string;
  count: number;
  memberIds: string[];
}

// Prod QA, 2026-09-16 (Fix 4) — every resolver below normalizes its step
// through `normalizeGongTokensInStep` first, so a mappable Gong token
// (`{{recipient.first_name}}`) resolves exactly like the Terra token it is an
// alias of. The server read seam cannot reach the ONE window this covers:
// copy the rep has just typed or PASTED and not yet saved, which these
// resolvers read straight out of the draft store. It lives here, not inside
// `_shared/launcher-copy-tokens.ts`, because that module is shared with
// launcher and must not import the Playbooks-owned Gong vocabulary. See
// `normalizeGongTokensInSteps`' doc for the rest.

/**
 * The interpolated subject/body for one sample member — `resolveStepCopy`,
 * the ONE step-shaped entry point the send path (`toOverrideSteps`) and
 * launcher's `buildMemberPayload` also go through.
 *
 * Was two `interpolateTemplate` calls, and that is where the preview/send
 * divergence lived (U2, Codex A4): the send resolved a SUBJECT in single-line
 * mode, `interpolateTemplate` forwarded no mode at all, and a `drop-line`
 * token in a subject therefore emptied the preview while merely shortening the
 * sent copy. R1 says those two strings must be one string; making the mode a
 * property of the field, resolved in one call for both fields, is what makes
 * that structurally true instead of a convention.
 *
 * `member === null` (no sample contact available — e.g. the campaign has no
 * synced members yet) returns `null` rather than guessing or throwing: the
 * caller (`SequencesStep.tsx`'s preview card) renders its own quiet
 * placeholder in that case instead of a fabricated interpolation.
 */
export function previewStepCopy(
  step: CopyPreviewStep,
  member: CampaignMember | null,
  campaign: CopyPreviewCampaign | null,
  sender: string | null,
): CopyPreviewResult | null {
  if (!member) return null;
  const senderValue = toSenderValue(sender);
  const resolved = resolveStepCopy(
    normalizeGongTokensInStep(step),
    buildTemplateVars(member, campaign, senderValue),
  );
  return { subject: resolved.subject.text, body: resolved.body.text };
}

/** `previewStepCopySegments`'s return shape — one `TemplateSegment[]` per copy field. */
export interface CopyPreviewSegments {
  subject: TemplateSegment[];
  body: TemplateSegment[];
}

/**
 * `previewStepCopy`'s segment-producing sibling (Launcher V2 C2, plan
 * `2026-09-01-001`) — same inputs, same `member === null` → `null` contract,
 * but reports each field as `TemplateSegment[]` instead of a plain string so
 * the copy preview can give a known-but-empty or unrecognized token a
 * warning treatment instead of silently rendering blank text or (for
 * `filled`) rendering it exactly like unrelated plain text. Reuses v1's
 * `interpolateTemplateSegments` verbatim — no separate segmentation logic.
 */
export function previewStepCopySegments(
  step: CopyPreviewStep,
  member: CampaignMember | null,
  campaign: CopyPreviewCampaign | null,
  sender: string | null,
): CopyPreviewSegments | null {
  if (!member) return null;
  const senderValue = toSenderValue(sender);
  const aliased = normalizeGongTokensInStep(step);
  return {
    subject: interpolateTemplateSegments(aliased.subject ?? '', member, campaign, senderValue),
    body: interpolateTemplateSegments(aliased.body ?? '', member, campaign, senderValue),
  };
}

/**
 * Token gaps for one step's copy against one sample member — v1's
 * `checkCopyVarGaps` wants a members array (it reports which named members
 * hit the gap); a single-sample check passes a one-element array, so the gap
 * vocabulary (`variable`/`count`/`memberNames`) is untouched.
 *
 * `member === null` returns `[]` — no member data to check against means no
 * flags to raise, not a crash.
 */
export function stepTokenGaps(
  step: CopyPreviewStep,
  member: CampaignMember | null,
  campaign: CopyPreviewCampaign | null,
  sender: string | null,
): CopyTokenGap[] {
  if (!member) return [];
  const senderValue = toSenderValue(sender);
  const aliased = normalizeGongTokensInStep(step);
  return checkCopyVarGaps(
    aliased.subject ?? '',
    aliased.body ?? '',
    [member],
    campaign,
    senderValue,
  );
}

/**
 * Sample-contact selection (plan's deferred question, resolved at U6 time):
 * the first rung-scoped matched member, roster order, falling back to the
 * first campaign member when nothing is scoped/matched yet. `null` (no
 * campaign members loaded at all) is the only case with no sample contact.
 *
 * `matchedIds === null` covers both "no scope set" and "the ladder hasn't
 * resolved / errored" — either way, falling back to the plain roster's first
 * row is the cheap, honest default (this is a preview aid, not a routing
 * claim like `MembersPanel`'s scoped table).
 */
export function selectSampleMember(
  members: readonly CampaignMember[],
  matchedIds: readonly string[] | null,
): CampaignMember | null {
  if (matchedIds && matchedIds.length > 0) {
    const matched = new Set(matchedIds);
    const firstMatched = members.find((member) => matched.has(member.id));
    if (firstMatched) return firstMatched;
  }
  return members[0] ?? null;
}

/**
 * U10 (item 19, R13, landmine 7) — the roster-wide replacement for the
 * single-sample check above. QA item 19's finding: checking one sample
 * member (`stepTokenGaps`, called with a one-element array at the
 * `SequencesStep.tsx` call site this supersedes) silently narrowed the
 * phases doc's "missing tokens flagged" guarantee — a gap that happened to
 * be filled for the sample contact went completely unreported for the other
 * 39 members of a 40-person rung. This function is that correction, not a
 * parallel/optional path: it is the ONLY token-gap check `SequencesStep`
 * calls now.
 *
 * Mirrors the prototype's `unresolvedTokenCounts(rung, mIdx)`
 * (`playbooks-prototype-v6.html:3592-3605`): every step's subject+body
 * feeds one combined pass (a token used only in step 3's body is exactly as
 * much a roster gap as one used in step 1's subject), and the result is
 * `{token, count, memberIds}` sorted by `count` descending.
 *
 * REUSE, DON'T RE-DERIVE (as this module's header already commits to): the
 * per-member resolution runs through `_shared`'s `checkCopyVarGapsByMemberId`
 * (U11/E1, Decision 14) — the id-keyed primitive
 * `checkCopyVarGaps` is itself now built on. It exists because
 * `checkCopyVarGaps`' aggregation keys gaps by `memberNames`, not
 * `member.id`, and two members can share a display name; this function needs
 * collision-free `memberIds`.
 *
 * It used to get those by calling `checkCopyVarGaps` once per member with a
 * one-element array, which re-scanned the whole joined subject+body M times
 * (the E1 typing lag). The primitive extracts the token set once, so the cost
 * is O(len + M·tokens) and this function is a pure shape transform over its
 * result. Output is unchanged — see the parity test.
 *
 * `members.length === 0` or `steps.length === 0` returns `[]` — no roster or
 * no copy to check means no gaps to raise, not a crash.
 */
export function rosterTokenGaps(
  steps: readonly CopyPreviewStep[],
  members: readonly CampaignMember[],
  campaign: CopyPreviewCampaign | null,
  sender: string | null,
): RosterTokenGap[] {
  if (members.length === 0 || steps.length === 0) return [];
  const senderValue = toSenderValue(sender);
  const aliased = normalizeGongTokensInSteps(steps);
  const subject = aliased.map((s) => s.subject ?? '').join('\n');
  const body = aliased.map((s) => s.body ?? '').join('\n');

  // Playbooks QA round 4, U11 (E1) / Decision 14 — ONE call that extracts the
  // `{{token}}` set once and then loops members, replacing the former
  // per-member `checkCopyVarGaps(subject, body, [member], …)` loop that
  // re-scanned the whole joined copy M times (the E1 typing lag on a 400-row
  // roster). Output is byte-identical — `copy-token-flags.test.ts` holds a
  // parity assertion against a preserved copy of the old implementation.
  const memberIdsByToken = checkCopyVarGapsByMemberId(subject, body, members, campaign, senderValue);

  return Array.from(memberIdsByToken.entries())
    .map(([token, memberIds]) => ({ token, count: memberIds.length, memberIds }))
    .sort((a, b) => b.count - a.count);
}

/**
 * U11 (E1) / Decision 14 — the INVERTED index of {@link rosterTokenGaps}:
 * `memberId -> token names`, built once per gaps array.
 *
 * Why: `MembersPanel` annotated EVERY rendered row with
 * `missingTokensForMember(gaps, member.id)`, whose `memberIds.includes(id)` is
 * a linear scan of a roster-sized array — so rendering R rows cost
 * O(R · gaps · M) per keystroke. This is O(gaps · M) once, then O(1) per row.
 *
 * A member with no gaps is ABSENT from the map rather than mapped to `[]`, so
 * the caller's `?? []` is the "no missing fields" branch (and the map stays
 * proportional to the members that actually have a gap). Token order within a
 * member follows `gaps`' own count-descending order, matching what
 * {@link missingTokensForMember}'s `filter` produced.
 */
export function missingTokensByMemberId(
  gaps: readonly RosterTokenGap[],
): Map<string, string[]> {
  const byMemberId = new Map<string, string[]>();
  for (const gap of gaps) {
    for (const memberId of gap.memberIds) {
      const tokens = byMemberId.get(memberId);
      if (tokens) tokens.push(gap.token);
      else byMemberId.set(memberId, [gap.token]);
    }
  }
  return byMemberId;
}

/**
 * One member's own missing-token names, sourced from the roster-wide `gaps`.
 *
 * U11 (E1) — kept as a thin per-member helper for callers that need exactly
 * one member's list (and for the tests that pin the two agree), but the
 * per-ROW render path now uses {@link missingTokensByMemberId} instead: this
 * function is a linear scan of `memberIds`, which is roster-sized.
 */
export function missingTokensForMember(gaps: readonly RosterTokenGap[], memberId: string): string[] {
  return gaps.filter((gap) => gap.memberIds.includes(memberId)).map((gap) => gap.token);
}

/** `sortMembersForScope`'s return shape — the reordered roster plus the ids that earned the warning treatment. */
export interface ScopeSortedMembers<M> {
  list: M[];
  unresolvedIds: ReadonlySet<string>;
}

/**
 * A3 (plan `2026-09-01-003`) — the "Missing fields" column's sort direction.
 * `'missing-first'` is the original, default behavior (see this function's
 * own doc below); `'missing-last'` is its negation. Lives here rather than as
 * a boolean so the two call sites (`sortMembersForScope`'s param,
 * `members-panel-collapse-store.ts`'s persisted state) share one vocabulary
 * instead of a `boolean` whose polarity has to be remembered at each site.
 */
export type GapSortDirection = 'missing-first' | 'missing-last';

/**
 * C3 (plan `2026-09-01-001`) — the prototype's unresolved-first roster
 * ordering, ported from `sortMembersForScope`
 * (`mini-apps/playbooks/docs/playbooks-prototype-v6.html:6871-6893`; that is
 * prototype JS, not repo code, so this is a port rather than a call into an
 * existing helper). Members whose copy leaves at least one merge token
 * unresolved sort to the top of `MembersPanel`'s table and are named in
 * `unresolvedIds` so their name cell can carry the warning icon.
 *
 * A stable sort (`Array.prototype.sort` is stable per spec since ES2019) on a
 * 0/1 key, so roster order is preserved WITHIN each of the two groups — the
 * table still reads as "the same rows, one group floated up", not as a
 * re-sort. `gaps` empty (nothing unresolved, or no mapping to check against)
 * returns the roster untouched and an empty set, which is what makes the
 * caller's "no gap column, no icons" branch a plain consequence of the data
 * rather than a second code path.
 *
 * Never mutates `members` — callers hand over a react-query-owned array.
 *
 * A3 (plan `2026-09-01-003`) — `direction` defaults to `'missing-first'`, the
 * original (and only, pre-A3) behavior, so every pre-existing call site and
 * test that omits the argument keeps behaving identically. `'missing-last'`
 * negates the comparator instead of re-deriving a second sort: the two groups
 * (unresolved / resolved) are the same `unresolvedIds` set either way — only
 * which group sorts first flips. `unresolvedIds` itself is returned unchanged
 * in both directions (the name-cell warning icon is not sort-dependent).
 */
export function sortMembersForScope<M extends { id: string }>(
  members: readonly M[],
  gaps: readonly RosterTokenGap[],
  direction: GapSortDirection = 'missing-first',
): ScopeSortedMembers<M> {
  if (gaps.length === 0) return { list: [...members], unresolvedIds: new Set() };
  const unresolvedIds = new Set<string>();
  for (const gap of gaps) for (const id of gap.memberIds) unresolvedIds.add(id);
  const unresolvedRank = direction === 'missing-first' ? 0 : 1;
  const resolvedRank = direction === 'missing-first' ? 1 : 0;
  const rankOf = (id: string) => (unresolvedIds.has(id) ? unresolvedRank : resolvedRank);
  const list = [...members].sort((a, b) => rankOf(a.id) - rankOf(b.id));
  return { list, unresolvedIds };
}

/**
 * The roster subset a roster-wide check should run against — mirrors
 * `members-panel-scope.ts#membersPanelState`'s `matched` branch (filter to
 * the matched-id set, roster order preserved) with one deliberate difference:
 * `matchedIds === null` here means "unsaved rung — no ladder entry exists yet"
 * (the same case `selectSampleMember` falls back on), so it returns the WHOLE
 * roster rather than an empty/error state. `matchedIds` as a real empty array
 * (`[]`) is a genuine zero-match scope and correctly filters to nothing.
 */
export function rosterMembersForGapCheck(
  members: readonly CampaignMember[],
  matchedIds: readonly string[] | null,
): CampaignMember[] {
  if (matchedIds === null) return [...members];
  const matched = new Set(matchedIds);
  return members.filter((member) => matched.has(member.id));
}
